import React, { useEffect, useState, useCallback } from "react";
import {
  TextField,
  MenuItem,
  Button,
  IconButton,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  makeStyles
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import PaymentIcon from "@material-ui/icons/Payment";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import ReceiptOutlinedIcon from "@material-ui/icons/ReceiptOutlined";
import { toast } from "react-toastify";
import api from "../../services/api";
import useConfirm from "../../hooks/useConfirm";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import StatusChip, { statusLabel } from "../../components/StatusChip";
import { displayClientName } from "../../utils/clientName";
import copyToClipboard from "../../utils/copyToClipboard";

const useStyles = makeStyles((theme) => ({
  field: { minWidth: 170 },
  search: { minWidth: 240 },
  descriptionCell: { display: "flex", flexDirection: "column", gap: 1 },
  muted: { color: theme.palette.text.hint },
  secondary: { color: theme.palette.text.secondary },
  overdue: { color: theme.palette.error.main, fontWeight: 600 },
  footerInfo: { color: theme.palette.text.secondary },
  pager: { display: "flex", alignItems: "center", gap: theme.spacing(1) }
}));

const fmt = (v, c = "BRL") =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: c });

const fmtDate = (v) => {
  if (!v) return null;
  const d = new Date(`${String(v).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("pt-BR");
};

const isOverdue = (row) => {
  if (["paid", "canceled", "refunded"].includes(row.status)) return false;
  if (!row.dueDate) return false;
  return String(row.dueDate).slice(0, 10) < new Date().toISOString().slice(0, 10);
};

/**
 * Página genérica para "Contas a Pagar" e "Contas a Receber".
 * Props:
 *  - kind: "payable" | "receivable"
 *  - title
 *  - endpoint (ex.: /finance/payables)
 *  - extraStatuses: lista para filtro
 */
const FinanceAccountListPage = ({ kind, title, endpoint }) => {
  const classes = useStyles();
  const [confirm, confirmDialog] = useConfirm();
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ searchParam: "", status: "", dueFrom: "", dueTo: "" });
  const isPayable = kind === "payable";

  const emptyForm = {
    description: "",
    categoryId: "",
    costCenterId: "",
    supplierName: "",
    clientId: "",
    amount: 0,
    dueDate: "",
    accountId: "",
    paymentMethod: "",
    notes: ""
  };

  const [dialog, setDialog] = useState({ open: false, form: emptyForm, editing: null });
  const [payDialog, setPayDialog] = useState({ open: false, target: null, amount: "", accountId: "", paymentMethod: "" });
  const [gateways, setGateways] = useState([]);
  const [chargeDialog, setChargeDialog] = useState({
    open: false,
    target: null,
    gatewayId: "",
    amount: "",
    paymentMethod: "pix",
    expiresAt: "",
    sendWhatsApp: true,
    ticketId: "",
    customMessage: ""
  });

  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { pageNumber: page, pageSize };
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) params[k] = v;
      });
      const { data } = await api.get(endpoint, { params });
      setRows(data.data || []);
      setTotal(data.pagination?.total || 0);
    } catch (e) {
      toast.error("Erro ao carregar lançamentos");
    } finally {
      setLoading(false);
    }
  }, [endpoint, filters, page, pageSize]);

  useEffect(() => {
    api.get("/finance/categories", { params: { kind: isPayable ? "expense" : "income" } })
      .then((r) => setCategories(r.data || [])).catch(() => {});
    api.get("/finance/accounts").then((r) => setAccounts(r.data || [])).catch(() => {});
    if (isPayable) api.get("/finance/cost-centers").then((r) => setCostCenters(r.data || [])).catch(() => {});
    if (!isPayable) api.get("/payment-gateways").then((r) => setGateways((r.data || []).filter((g) => g.active))).catch(() => {});
  }, [isPayable]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => setDialog({ open: true, form: emptyForm, editing: null });
  const openEdit = (r) =>
    setDialog({
      open: true,
      form: {
        ...emptyForm,
        ...r,
        categoryId: r.categoryId ?? "",
        accountId: r.accountId ?? "",
        costCenterId: r.costCenterId ?? "",
        clientId: r.clientId ?? ""
      },
      editing: r
    });
  const close = () => setDialog((d) => ({ ...d, open: false }));

  const save = async () => {
    try {
      const payload = { ...dialog.form };
      ["categoryId", "accountId", "costCenterId", "clientId"].forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      payload.amount = Number(payload.amount) || 0;
      if (dialog.editing) {
        await api.put(`${endpoint}/${dialog.editing.id}`, payload);
        toast.success("Atualizado");
      } else {
        await api.post(endpoint, payload);
        toast.success("Criado");
      }
      close();
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erro ao salvar");
    }
  };

  const remove = async (r) => {
    if (!(await confirm({ title: `Excluir o lançamento "${r.description}"?`, message: "Se já houve baixa, a movimentação na conta financeira não é revertida automaticamente." }))) return;
    try {
      await api.delete(`${endpoint}/${r.id}`);
      toast.success("Removido");
      fetchData();
    } catch (e) {
      toast.error("Erro ao excluir");
    }
  };

  const openPay = (r) =>
    setPayDialog({
      open: true,
      target: r,
      amount: r.netAmount || r.amount,
      accountId: r.accountId || "",
      paymentMethod: r.paymentMethod || ""
    });

  const doPay = async () => {
    try {
      await api.post(`${endpoint}/${payDialog.target.id}/pay`, {
        accountId: Number(payDialog.accountId),
        amount: Number(payDialog.amount),
        paymentMethod: payDialog.paymentMethod || null
      });
      toast.success(isPayable ? "Pagamento registrado" : "Recebimento registrado");
      setPayDialog({ open: false, target: null, amount: "", accountId: "", paymentMethod: "" });
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erro ao registrar baixa");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const openCharge = (r) => {
    const remaining = Number(r.amount || 0) - Number(r.paidAmount || 0);
    setChargeDialog({
      open: true,
      target: r,
      gatewayId: gateways[0]?.id || "",
      amount: remaining > 0 ? remaining : r.amount,
      paymentMethod: "pix",
      expiresAt: r.dueDate || "",
      sendWhatsApp: true,
      ticketId: "",
      customMessage: ""
    });
  };

  const doCharge = async () => {
    try {
      const payload = {
        gatewayId: Number(chargeDialog.gatewayId),
        receivableId: chargeDialog.target.id,
        clientId: chargeDialog.target.clientId || null,
        amount: Number(chargeDialog.amount),
        paymentMethod: chargeDialog.paymentMethod,
        expiresAt: chargeDialog.expiresAt || null,
        sendWhatsApp: !!chargeDialog.sendWhatsApp,
        ticketId: chargeDialog.ticketId ? Number(chargeDialog.ticketId) : null,
        customMessage: chargeDialog.customMessage || null,
        description: chargeDialog.target.description
      };
      const { data } = await api.post("/payment-charges", payload);
      toast.success(
        data?.charge?.paymentLink || data?.charge?.pixCode
          ? "Cobrança criada"
          : "Cobrança registrada"
      );
      if (data?.charge?.paymentLink) {
        copyToClipboard(data.charge.paymentLink);
      } else if (data?.charge?.pixCode) {
        copyToClipboard(data.charge.pixCode);
      }
      setChargeDialog({ ...chargeDialog, open: false });
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erro ao gerar cobrança");
    }
  };

  const dash = <span className={classes.muted}>—</span>;

  const statusOptions = isPayable
    ? ["pending", "partially_paid", "paid", "overdue", "canceled"]
    : ["open", "awaiting", "partially_paid", "paid", "overdue", "canceled", "refunded"];

  const hasFilters = Object.values(filters).some((v) => v !== "" && v != null);

  const columns = [
    {
      key: "description",
      label: "Descrição",
      render: (r) => (
        <div className={classes.descriptionCell}>
          <Typography variant="subtitle2">{r.description || dash}</Typography>
          {r.notes && (
            <Typography variant="caption" className={classes.secondary} noWrap>
              {r.notes}
            </Typography>
          )}
        </div>
      )
    },
    {
      key: "counterparty",
      label: isPayable ? "Fornecedor" : "Cliente",
      // `r.client.name` não existe no model Client — a coluna renderizava "-"
      // para todo lançamento. Ver utils/clientName.
      render: (r) =>
        (isPayable ? r.supplierName : displayClientName(r.client, "")) || dash
    },
    { key: "category", label: "Categoria", render: (r) => r.category?.name || dash },
    { key: "amount", label: "Valor", type: "numeric", render: (r) => fmt(r.amount) },
    {
      key: "paidAmount",
      label: "Pago",
      type: "numeric",
      render: (r) =>
        Number(r.paidAmount || 0) > 0 ? (
          fmt(r.paidAmount)
        ) : (
          <span className={classes.muted}>{fmt(0)}</span>
        )
    },
    {
      key: "dueDate",
      label: "Vence",
      render: (r) => (
        <span className={isOverdue(r) ? classes.overdue : undefined}>
          {fmtDate(r.dueDate) || dash}
        </span>
      )
    },
    {
      key: "status",
      label: "Status",
      render: (r) => <StatusChip dot status={isOverdue(r) ? "overdue" : r.status} />
    },
    {
      key: "actions",
      label: "Ações",
      type: "actions",
      render: (r) => (
        <>
          {!isPayable &&
            ["open", "awaiting", "partially_paid", "overdue"].includes(r.status) &&
            gateways.length > 0 && (
              <Tooltip title="Gerar cobrança via gateway">
                <IconButton size="small" onClick={() => openCharge(r)}>
                  <AttachMoneyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          {!["paid", "canceled", "refunded"].includes(r.status) && (
            <Tooltip title={isPayable ? "Registrar pagamento" : "Registrar recebimento"}>
              <IconButton size="small" onClick={() => openPay(r)}>
                <PaymentIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Editar">
            <IconButton size="small" onClick={() => openEdit(r)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir">
            <IconButton size="small" onClick={() => remove(r)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      )
    }
  ];

  return (
    <MainContainer>
      {confirmDialog}
      <PageHeader
        title={title}
        count={total}
        subtitle={
          isPayable
            ? "Obrigações a pagar, com baixa manual e conciliação por conta financeira."
            : "Valores a receber, com baixa manual ou cobrança gerada no gateway."
        }
        actions={
          <Button color="primary" variant="contained" startIcon={<AddIcon />} onClick={openNew}>
            Novo lançamento
          </Button>
        }
        filters={
          <>
            <TextField className={classes.search} label="Buscar"
              value={filters.searchParam}
              onChange={(e) => { setPage(1); setFilters({ ...filters, searchParam: e.target.value }); }} />
            <TextField className={classes.field} select label="Status"
              value={filters.status}
              onChange={(e) => { setPage(1); setFilters({ ...filters, status: e.target.value }); }}>
              <MenuItem value="">Todos</MenuItem>
              {statusOptions.map((s) => (
                <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>
              ))}
            </TextField>
            <TextField className={classes.field} type="date" label="Vence de"
              InputLabelProps={{ shrink: true }} value={filters.dueFrom}
              onChange={(e) => { setPage(1); setFilters({ ...filters, dueFrom: e.target.value }); }} />
            <TextField className={classes.field} type="date" label="Vence até"
              InputLabelProps={{ shrink: true }} value={filters.dueTo}
              onChange={(e) => { setPage(1); setFilters({ ...filters, dueTo: e.target.value }); }} />
          </>
        }
      />

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        emptyIcon={<ReceiptOutlinedIcon />}
        emptyTitle={hasFilters ? "Nenhum lançamento encontrado" : "Nenhum lançamento registrado"}
        emptyDescription={
          hasFilters
            ? "Nenhum lançamento atende aos filtros aplicados. Ajuste o período ou o status."
            : `Registre o primeiro lançamento de ${isPayable ? "contas a pagar" : "contas a receber"} para acompanhar o fluxo de caixa.`
        }
        emptyAction={
          !hasFilters ? (
            <Button color="primary" variant="contained" startIcon={<AddIcon />} onClick={openNew}>
              Novo lançamento
            </Button>
          ) : null
        }
        footer={
          <>
            <Typography variant="body2" className={classes.footerInfo}>
              {total} {total === 1 ? "lançamento" : "lançamentos"}
            </Typography>
            <div className={classes.pager}>
              <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Anterior
              </Button>
              <Typography variant="body2" className={classes.footerInfo}>
                {page} / {totalPages}
              </Typography>
              <Button size="small" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                Próxima
              </Button>
            </div>
          </>
        }
      />

      {/* Form dialog */}
      <Dialog open={dialog.open} onClose={close} maxWidth="md" fullWidth>
        <DialogTitle>{dialog.editing ? "Editar lançamento" : "Novo lançamento"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: 4 }}>
            <Grid item xs={12} sm={8}>
              <TextField fullWidth size="small" variant="outlined" label="Descrição *"
                value={dialog.form.description}
                onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, description: e.target.value } })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" type="number" variant="outlined" label="Valor *"
                value={dialog.form.amount}
                onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, amount: e.target.value } })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" type="date" variant="outlined" label="Vencimento *"
                InputLabelProps={{ shrink: true }} value={dialog.form.dueDate || ""}
                onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, dueDate: e.target.value } })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth select size="small" variant="outlined" label="Categoria"
                value={dialog.form.categoryId ?? ""}
                onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, categoryId: e.target.value } })}>
                <MenuItem value="">—</MenuItem>
                {categories.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth select size="small" variant="outlined" label="Conta padrão"
                value={dialog.form.accountId ?? ""}
                onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, accountId: e.target.value } })}>
                <MenuItem value="">—</MenuItem>
                {accounts.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
              </TextField>
            </Grid>
            {isPayable ? (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" variant="outlined" label="Fornecedor"
                    value={dialog.form.supplierName || ""}
                    onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, supplierName: e.target.value } })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth select size="small" variant="outlined" label="Centro de custo"
                    value={dialog.form.costCenterId ?? ""}
                    onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, costCenterId: e.target.value } })}>
                    <MenuItem value="">—</MenuItem>
                    {costCenters.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                  </TextField>
                </Grid>
              </>
            ) : (
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" type="number" variant="outlined" label="Cliente (ID)"
                  value={dialog.form.clientId || ""}
                  onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, clientId: e.target.value } })} />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" variant="outlined" label="Forma de pagamento"
                value={dialog.form.paymentMethod || ""}
                onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, paymentMethod: e.target.value } })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={2} size="small" variant="outlined" label="Notas"
                value={dialog.form.notes || ""}
                onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, notes: e.target.value } })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancelar</Button>
          <Button color="primary" variant="contained" onClick={save}>Salvar</Button>
        </DialogActions>
      </Dialog>

      {/* Pay dialog */}
      <Dialog open={payDialog.open} onClose={() => setPayDialog({ ...payDialog, open: false })} maxWidth="xs" fullWidth>
        <DialogTitle>{isPayable ? "Registrar pagamento" : "Registrar recebimento"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: 4 }}>
            <Grid item xs={12}>
              <TextField fullWidth select size="small" variant="outlined" label="Conta financeira *"
                value={payDialog.accountId}
                onChange={(e) => setPayDialog({ ...payDialog, accountId: e.target.value })}>
                <MenuItem value="">—</MenuItem>
                {accounts.map((a) => <MenuItem key={a.id} value={a.id}>{a.name} — {fmt(a.currentBalance, a.currency)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth type="number" size="small" variant="outlined" label="Valor *"
                value={payDialog.amount}
                onChange={(e) => setPayDialog({ ...payDialog, amount: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" variant="outlined" label="Forma de pagamento"
                value={payDialog.paymentMethod}
                onChange={(e) => setPayDialog({ ...payDialog, paymentMethod: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayDialog({ ...payDialog, open: false })}>Cancelar</Button>
          <Button color="primary" variant="contained" onClick={doPay} disabled={!payDialog.accountId}>Confirmar</Button>
        </DialogActions>
      </Dialog>

      {/* Charge dialog (only receivables) */}
      {!isPayable && (
        <Dialog open={chargeDialog.open} onClose={() => setChargeDialog({ ...chargeDialog, open: false })} maxWidth="sm" fullWidth>
          <DialogTitle>Gerar cobrança via gateway</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} style={{ marginTop: 4 }}>
              <Grid item xs={12} sm={8}>
                <TextField fullWidth select size="small" variant="outlined" label="Gateway *"
                  value={chargeDialog.gatewayId}
                  onChange={(e) => setChargeDialog({ ...chargeDialog, gatewayId: e.target.value })}>
                  <MenuItem value="">—</MenuItem>
                  {gateways.map((g) => (
                    <MenuItem key={g.id} value={g.id}>{g.name} ({g.provider}{g.sandbox ? "/sandbox" : ""})</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth select size="small" variant="outlined" label="Forma *"
                  value={chargeDialog.paymentMethod}
                  onChange={(e) => setChargeDialog({ ...chargeDialog, paymentMethod: e.target.value })}>
                  <MenuItem value="pix">PIX</MenuItem>
                  <MenuItem value="boleto">Boleto</MenuItem>
                  <MenuItem value="credit_card">Crédito</MenuItem>
                  <MenuItem value="debit_card">Débito</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" type="number" variant="outlined" label="Valor *"
                  value={chargeDialog.amount}
                  onChange={(e) => setChargeDialog({ ...chargeDialog, amount: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" type="date" variant="outlined" label="Vencimento"
                  InputLabelProps={{ shrink: true }} value={chargeDialog.expiresAt || ""}
                  onChange={(e) => setChargeDialog({ ...chargeDialog, expiresAt: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth select size="small" variant="outlined" label="Enviar via WhatsApp"
                  value={chargeDialog.sendWhatsApp ? "1" : "0"}
                  onChange={(e) => setChargeDialog({ ...chargeDialog, sendWhatsApp: e.target.value === "1" })}>
                  <MenuItem value="1">Sim</MenuItem>
                  <MenuItem value="0">Não</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" type="number" variant="outlined" label="Ticket ID (opcional)"
                  value={chargeDialog.ticketId || ""}
                  onChange={(e) => setChargeDialog({ ...chargeDialog, ticketId: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline minRows={2} size="small" variant="outlined"
                  label="Mensagem personalizada (opcional)"
                  value={chargeDialog.customMessage || ""}
                  onChange={(e) => setChargeDialog({ ...chargeDialog, customMessage: e.target.value })} />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setChargeDialog({ ...chargeDialog, open: false })}>Cancelar</Button>
            <Button color="primary" variant="contained" onClick={doCharge}
              disabled={!chargeDialog.gatewayId || !chargeDialog.amount}>Gerar</Button>
          </DialogActions>
        </Dialog>
      )}
    </MainContainer>
  );
};

export default FinanceAccountListPage;
